import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter,
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  FormGroup,
  Label,
  Grid
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { OfficeBudget, OfficeBudgetCategory, BudgetCategoryType } from '../../types';
import { FaTrash as FaTrashIcon, FaPlus as FaPlusIcon } from 'react-icons/fa';
import apiService from '../../services/api';
import { formatCurrency } from '../../utils/formatUtils';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div<{ type?: string }>`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  text-align: center;
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  .amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${props => props.type === 'budget' ? 'var(--primary-color)' : 
      props.type === 'spent' ? 'var(--danger-color)' : 
      props.type === 'remaining' ? 'var(--success-color)' : 'var(--text-color)'};
  }
  
  .label {
    font-size: 0.85rem;
    color: var(--dark-gray);
    margin-top: 0.5rem;
  }
`;

const ProgressBar = styled.div<{ percentage: number }>`
  width: 100%;
  height: 8px;
  background-color: var(--light-gray);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
  
  .progress {
    height: 100%;
    background-color: ${props => props.percentage > 100 ? 'var(--danger-color)' : 
      props.percentage > 80 ? 'var(--warning-color)' : 'var(--success-color)'};
    width: ${props => Math.min(props.percentage, 100)}%;
    transition: width 0.3s ease;
  }
`;

const CategoryRow = styled(TableRow)<{ isOverBudget: boolean }>`
  background-color: ${props => props.isOverBudget ? 'rgba(220, 53, 69, 0.1)' : 'transparent'};
  
  &:hover {
    background-color: ${props => props.isOverBudget ? 'rgba(220, 53, 69, 0.15)' : 'var(--light-gray)'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const ModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.15);
  padding: 2.5rem 2.5rem 2rem 2.5rem;
  min-width: 480px;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;
const ModalTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 2rem;
  font-size: 1.4rem;
  text-align: center;
`;
const ModalActions = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: flex-end;
  margin-top: 2.5rem;
`;
const ModalField = styled.div`
  margin-bottom: 1.5rem;
`;

const Budget: React.FC = () => {
  const { user } = useAuth();
  const [budget, setBudget] = useState<OfficeBudget | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [accountingEntries, setAccountingEntries] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    categories: [] as OfficeBudgetCategory[]
  });

  // Modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<OfficeBudgetCategory | null>(null);
  const [addForm, setAddForm] = useState({ name: '', budgetedAmount: 0, spentAmount: 0 });
  const [editForm, setEditForm] = useState({ name: '', budgetedAmount: 0, spentAmount: 0 });

  useEffect(() => {
    const loadBudgetData = async () => {
      try {
        setLoading(true);
        
        // Load real accounting entries
        const entriesResponse = await apiService.getAccountingEntries({ limit: 1000 });
        const entries = entriesResponse.data || [];
        setAccountingEntries(entries);
        
        // Calculate real totals from accounting entries
        const totalIncome = entries
          .filter((entry: any) => entry.type === 'Income')
          .reduce((sum: number, entry: any) => sum + entry.amount, 0);
        
        const totalExpenses = entries
          .filter((entry: any) => entry.type === 'Expense')
          .reduce((sum: number, entry: any) => sum + entry.amount, 0);
        
        // Load office budget requests to calculate budget data
        const response = await apiService.getOfficeBudgetRequests({ limit: 1000 });
        const budgetRequests = response.data || [];
        
        // Use real budget categories from BudgetCategoryType
        const budgetCategories: BudgetCategoryType[] = [
          'Rent', 'Stationary', 'Salary', 'CRDB/ERB Fees', 'POSTA', 
          'Electricity', 'Communication', 'Emergency', 'Parking', 
          'Tendering Fees', 'Audit Fees', 'Others'
        ];
        
        // Calculate budget categories from real data
        const categoriesMap = new Map<string, any>();
        
        // Initialize all budget categories
        budgetCategories.forEach(categoryName => {
          categoriesMap.set(categoryName, {
            id: categoryName,
            name: categoryName,
            budgetedAmount: 0,
            spentAmount: 0,
            remainingAmount: 0,
            percentage: 0
          });
        });
        
        // Add data from budget requests
        budgetRequests.forEach((request: any) => {
          request.categories?.forEach((category: any) => {
            const existing = categoriesMap.get(category.name);
            if (existing) {
              existing.budgetedAmount += category.amount || 0;
              if (request.status === 'Paid') {
                existing.spentAmount += category.amount || 0;
              }
            }
          });
        });
        
        // Add data from accounting entries for spent amounts
        entries.forEach((entry: any) => {
          if (entry.type === 'Expense' && entry.category) {
            const existing = categoriesMap.get(entry.category);
            if (existing) {
              existing.spentAmount += entry.amount || 0;
            }
          }
        });
        
        // Convert map to array and calculate percentages
        const categories = Array.from(categoriesMap.values())
          .filter(category => category.budgetedAmount > 0 || category.spentAmount > 0) // Only show categories with data
          .map(category => ({
            ...category,
            percentage: category.budgetedAmount > 0 ? (category.spentAmount / category.budgetedAmount) * 100 : 0,
            remainingAmount: category.budgetedAmount - category.spentAmount
          }));
        
        const totalBudget = categories.reduce((sum, cat) => sum + cat.budgetedAmount, 0);
        
        const budgetData = {
          id: 'BUDGET-2024-01',
          year: 2024,
          month: 1,
          categories,
          totalBudget,
          currency: 'TZS',
          createdBy: 'System',
          createdAt: new Date(),
          approved: true,
          approvedBy: 'System',
          approvedAt: new Date()
        };
        
        setBudget(budgetData);
        setFormData({
          year: budgetData.year,
          month: budgetData.month,
          categories: budgetData.categories
        });
      } catch (error) {
        console.error('Error loading budget data:', error);
        // Fallback to empty budget
        const emptyBudget = {
          id: 'BUDGET-2024-01',
          year: 2024,
          month: 1,
          categories: [],
          totalBudget: 0,
          currency: 'TZS',
          createdBy: 'System',
          createdAt: new Date(),
          approved: true,
          approvedBy: 'System',
          approvedAt: new Date()
        };
        setBudget(emptyBudget);
        setFormData({
          year: emptyBudget.year,
          month: emptyBudget.month,
          categories: emptyBudget.categories
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadBudgetData();
  }, []);



  const calculateTotals = () => {
    if (!budget) return { totalBudget: 0, totalSpent: 0, totalReceived: 0 };
    
    const totalBudget = budget.categories.reduce((sum, cat) => sum + cat.budgetedAmount, 0);
    
    // Use real accounting data for spent and received
    const totalSpent = accountingEntries
      .filter((entry: any) => entry.type === 'Expense')
      .reduce((sum: number, entry: any) => sum + entry.amount, 0);
    
    const totalReceived = accountingEntries
      .filter((entry: any) => entry.type === 'Income')
      .reduce((sum: number, entry: any) => sum + entry.amount, 0);
    
    return { totalBudget, totalSpent, totalReceived };
  };

  const handleCategoryChange = (categoryId: string, field: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(cat => {
        if (cat.id === categoryId) {
          const updated = { ...cat, [field]: value };
          // Recalculate remaining amount and percentage
          updated.remainingAmount = updated.budgetedAmount - updated.spentAmount;
          updated.percentage = updated.budgetedAmount > 0 ? (updated.spentAmount / updated.budgetedAmount) * 100 : 0;
          return updated;
        }
        return cat;
      })
    }));
  };

  const handleCategoryNameChange = (categoryId: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(cat =>
        cat.id === categoryId ? { ...cat, name: value } : cat
      )
    }));
  };

  const handleRemoveCategory = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(cat => cat.id !== categoryId)
    }));
  };

  const handleAddCategory = () => {
    setFormData(prev => ({
      ...prev,
      categories: [
        ...prev.categories,
        {
          id: Date.now().toString(),
          name: '',
          budgetedAmount: 0,
          spentAmount: 0,
          remainingAmount: 0,
          percentage: 0
        }
      ]
    }));
  };

  const handleSave = async () => {
    try {
      if (budget) {
        // Update existing budget
        await apiService.updateOfficeBudget(budget.id, formData);
      } else {
        // Create new budget
        await apiService.createOfficeBudget(formData);
      }
      
      setEditing(false);
      // Reload budget data
      const response = await apiService.getOfficeBudget();
      setBudget(response.data);
      
      alert('Budget updated successfully');
    } catch (error) {
      console.error('Error saving budget:', error);
      alert('Failed to save budget. Please try again.');
    }
  };

  // Add Category Modal handlers
  const openAddModal = () => {
    setAddForm({ name: '', budgetedAmount: 0, spentAmount: 0 });
    setAddModalOpen(true);
  };
  const closeAddModal = () => setAddModalOpen(false);
  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.type === 'number' ? Number(e.target.value) : e.target.value });
  };
  const handleAddCategoryModal = () => {
    setFormData(prev => ({
      ...prev,
      categories: [
        ...prev.categories,
        {
          id: Date.now().toString(),
          name: addForm.name,
          budgetedAmount: addForm.budgetedAmount,
          spentAmount: addForm.spentAmount,
          remainingAmount: addForm.budgetedAmount - addForm.spentAmount,
          percentage: addForm.budgetedAmount > 0 ? (addForm.spentAmount / addForm.budgetedAmount) * 100 : 0
        }
      ]
    }));
    setAddModalOpen(false);
  };

  // Edit Category Modal handlers
  const openEditModal = (cat: OfficeBudgetCategory) => {
    setEditCategory(cat);
    setEditForm({ name: cat.name, budgetedAmount: cat.budgetedAmount, spentAmount: cat.spentAmount });
    setEditModalOpen(true);
  };
  const closeEditModal = () => setEditModalOpen(false);
  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.type === 'number' ? Number(e.target.value) : e.target.value });
  };
  const handleEditCategoryModal = () => {
    if (!editCategory) return;
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(cat =>
        cat.id === editCategory.id
          ? {
              ...cat,
              name: editForm.name,
              budgetedAmount: editForm.budgetedAmount,
              spentAmount: editForm.spentAmount,
              remainingAmount: editForm.budgetedAmount - editForm.spentAmount,
              percentage: editForm.budgetedAmount > 0 ? (editForm.spentAmount / editForm.budgetedAmount) * 100 : 0
            }
          : cat
      )
    }));
    setEditModalOpen(false);
  };

  if (loading) {
    return <div>Loading budget data...</div>;
  }

  if (!budget) {
    return (
      <div>
        <PageHeader>
          <h1 className="page-title">Office Budget</h1>
          <Button onClick={() => setEditing(true)}>Create Budget</Button>
        </PageHeader>
        <Card>
          <CardBody>
            <p>No budget data found. Create a new budget to get started.</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  const { totalBudget, totalSpent, totalReceived } = calculateTotals();
  const utilizationPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

  return (
    <div>
      <PageHeader>
        <div>
          <h1 className="page-title">Office Budget</h1>
          <p style={{ color: 'var(--dark-gray)', margin: '0.5rem 0 0 0' }}>
            {budget.year} - Month {budget.month}
          </p>
        </div>
        <FlexContainer gap="0.5rem">
          <OutlineButton as={Link} to="/office">
            Back to Office
          </OutlineButton>
          <Button as={Link} to="/office/budget-approval">
            Budget Approval
          </Button>
          {user?.role === 'MD' || user?.role === 'TD' || user?.role === 'Accountant' ? (
            <Button onClick={() => setEditing(!editing)}>
              {editing ? 'Cancel' : 'Edit Budget'}
            </Button>
          ) : null}
        </FlexContainer>
      </PageHeader>

      <Summary>
        <SummaryCard type="budget">
          <h3>Total Budget</h3>
          <div className="amount">{formatCurrency(totalBudget, budget.currency)}</div>
          <div className="label">Planned spending</div>
        </SummaryCard>
        
        <SummaryCard type="spent">
          <h3>Total Spent</h3>
          <div className="amount">{formatCurrency(totalSpent, budget.currency)}</div>
          <div className="label">Total expenses</div>
        </SummaryCard>
        
        <SummaryCard type="remaining">
          <h3>Received</h3>
          <div className="amount">{formatCurrency(totalReceived, budget.currency)}</div>
          <div className="label">Total income</div>
        </SummaryCard>
        
        <SummaryCard>
          <h3>Utilization</h3>
          <div className="amount">{utilizationPercentage.toFixed(1)}%</div>
          <div className="label">Budget utilization</div>
          <ProgressBar percentage={utilizationPercentage}>
            <div className="progress"></div>
          </ProgressBar>
        </SummaryCard>
      </Summary>

      <Card>
        <CardHeader>
          <h3>Budget Categories</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
            {budget.approved && <Badge variant="success">Approved</Badge>}
            {editing && (
              <Button type="button" style={{ background: 'var(--success-color)', color: '#fff', minWidth: 120, fontWeight: 600 }} onClick={openAddModal}>
                <span style={{ marginRight: 6 }}>＋</span> Add Budget
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Category</TableHeader>
                  <TableHeader>Budgeted</TableHeader>
                  <TableHeader>Spent</TableHeader>
                  <TableHeader>Remaining</TableHeader>
                  <TableHeader>Progress</TableHeader>
                  <TableHeader>Status</TableHeader>
                  {editing && <TableHeader>Action</TableHeader>}
                </TableRow>
              </TableHead>
              <tbody>
                {formData.categories.map((category) => (
                  <CategoryRow key={category.id} isOverBudget={category.percentage > 100}>
                    <TableCell>
                      <strong>{category.name}</strong>
                    </TableCell>
                    <TableCell>
                      {formatCurrency(category.budgetedAmount, budget.currency)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(category.spentAmount, budget.currency)}
                    </TableCell>
                    <TableCell>
                      <span style={{ 
                        color: category.remainingAmount < 0 ? 'var(--danger-color)' : 
                          category.remainingAmount === 0 ? 'var(--warning-color)' : 'var(--success-color)',
                        fontWeight: '500'
                      }}>
                        {formatCurrency(category.remainingAmount, budget.currency)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                          {category.percentage.toFixed(1)}%
                        </span>
                        <ProgressBar percentage={category.percentage}>
                          <div className="progress"></div>
                        </ProgressBar>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={
                          category.percentage > 100 ? 'danger' :
                          category.percentage === 100 ? 'warning' :
                          category.percentage > 80 ? 'info' : 'success'
                        }
                      >
                        {category.percentage > 100 ? 'Over Budget' :
                         category.percentage === 100 ? 'Complete' :
                         category.percentage > 80 ? 'High Usage' : 'On Track'}
                      </Badge>
                    </TableCell>
                    {editing && (
                      <TableCell style={{ display: 'flex', gap: 8 }}>
                        <Button type="button" style={{ background: 'var(--info-color)', color: '#fff', padding: '0.3rem 0.7rem' }} onClick={() => openEditModal(category)}>
                          Edit
                        </Button>
                        <Button type="button" style={{ background: 'var(--danger-color)', color: '#fff', padding: '0.3rem 0.7rem' }} onClick={() => handleRemoveCategory(category.id)}>
                          🗑️
                        </Button>
                      </TableCell>
                    )}
                  </CategoryRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </CardBody>
        
        {editing && (
          <CardFooter>
            <FlexContainer gap="0.5rem" justify="flex-end">
              <OutlineButton onClick={() => setEditing(false)}>
                Cancel
              </OutlineButton>
              <Button onClick={handleSave}>
                Save Changes
              </Button>
            </FlexContainer>
          </CardFooter>
        )}
      </Card>
      {/* Add Category Modal */}
      {addModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Add Budget Category</ModalTitle>
            <ModalField>
              <Label>Name</Label>
              <Input name="name" value={addForm.name} onChange={handleAddFormChange} placeholder="Category name" />
            </ModalField>
            <ModalField>
              <Label>Budgeted Amount</Label>
              <Input name="budgetedAmount" type="number" value={addForm.budgetedAmount} onChange={handleAddFormChange} />
            </ModalField>
            <ModalField>
              <Label>Spent Amount</Label>
              <Input name="spentAmount" type="number" value={addForm.spentAmount} onChange={handleAddFormChange} />
            </ModalField>
            <ModalActions>
              <Button type="button" onClick={closeAddModal} style={{ background: 'var(--gray)', color: '#222', minWidth: 100 }}>Cancel</Button>
              <Button type="button" onClick={handleAddCategoryModal} style={{ background: 'var(--success-color)', color: '#fff', minWidth: 120, fontWeight: 600 }}>Add</Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
      {/* Edit Category Modal */}
      {editModalOpen && editCategory && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Edit Budget Category</ModalTitle>
            <ModalField>
              <Label>Name</Label>
              <Input name="name" value={editForm.name} onChange={handleEditFormChange} placeholder="Category name" />
            </ModalField>
            <ModalField>
              <Label>Budgeted Amount</Label>
              <Input name="budgetedAmount" type="number" value={editForm.budgetedAmount} onChange={handleEditFormChange} />
            </ModalField>
            <ModalField>
              <Label>Spent Amount</Label>
              <Input name="spentAmount" type="number" value={editForm.spentAmount} onChange={handleEditFormChange} />
            </ModalField>
            <ModalActions>
              <Button type="button" onClick={closeEditModal} style={{ background: 'var(--gray)', color: '#222', minWidth: 100 }}>Cancel</Button>
              <Button type="button" onClick={handleEditCategoryModal} style={{ background: 'var(--info-color)', color: '#fff', minWidth: 120, fontWeight: 600 }}>Save</Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default Budget; 